import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react"
import { Platform } from "react-native"

const STORAGE_KEY = "inscription_funnel_state_v1"

export const INSCRIPTION_FUNNEL_INITIAL = {
    funnelStateId: null,
    // Où CE dossier choisit son format (`same_page` | `own_step`), tel que le
    // serveur l'a tiré. Il suit l'identifiant, par onglet : le store, lui, est
    // partagé entre les onglets et dirait la disposition du dernier dossier
    // ouvert ailleurs.
    formatStep: null,
    // Pré-rempli depuis la LP (submissionGUID HubSpot ou query params).
    studentFirstname: "",
    parentEmail: "",
    submissionGUID: null,
    leadIntent: null,
    // Étape classe — UNE seule question, contrairement à l'été : ce funnel
    // vend dans l'année en cours, seule la classe actuelle importe.
    studentClass: null,
    // Étape diagnostic — les spécificités de l'élève, chips dys/TDAH/HPI…,
    // optionnel, « Aucune » exclusive. Atterrit sur les besoins DURABLES de
    // l'élève, pas sur la demande. Le niveau global n'est plus demandé : le
    // curseur par matière du compositeur dit où le parent veut aller.
    besoinsSpeciaux: [],
    // Matières choisies au compositeur (ids en STRING, comme les chips).
    subjects: [],
    // L'objectif visé PAR MATIÈRE ({ id matière: cran }), mêmes crans que le
    // stage. Il remplace le niveau ressenti et se traduit en besoin sur
    // l'objectif pédagogique de chaque matière à l'encaissement.
    subjectGoals: {},
    // Étape configuration — mode de cours ("essential" partagé / "integral"
    // particulier) et créneaux au format LOCAL du parent ("Monday 17h-18h",
    // une entrée par heure). Le volume hebdomadaire n'est pas stocké : il se
    // déduit du nombre de créneaux, et c'est le serveur qui fait foi.
    offerType: null,
    // Volume ACHETÉ, saisi par le parent — plus une déduction du nombre de
    // créneaux, qui confondait « combien j'achète » et « quand je suis libre ».
    weeklyHours: 0,
    // Disponibilités, au format LOCAL du parent. Sans conséquence tarifaire :
    // on en veut plus que d'heures achetées.
    slots: [],
    // Répartition optionnelle des heures entre matières ({subjectId: heures}).
    hoursAllocation: {},
    // Date de démarrage choisie ("YYYY-MM-DD"), bornée des deux côtés.
    startDate: null,
    // L'engagement DEMANDÉ par le parent (l'interrupteur de l'étape formule).
    // Ce qui s'affiche comme engagé ou non se lit sur le devis, pas ici.
    withCommitment: true,
    // La formule engagée qu'il avait composée avant de couper l'engagement
    // ({ offerType, weeklyHours, hoursAllocation }), pour la lui rendre s'il
    // le remet. Le serveur ne la connaît pas : une reprise par lien repart
    // sans elle.
    committedFormula: null,
    // ⚠️ Rien ici sur la CONFIG du funnel (bornes de date, volume max, FAQ).
    // Elle est servie par le backend sur les trois chemins d'entrée (init,
    // start, resume) et vit dans le store `data.inscriptionFunnel`, alimenté
    // automatiquement par le reducer api. La porter aussi ici en faisait deux
    // copies dont une seule était réhydratée à la reprise — asymétrie qui ne se
    // voyait pas, parce que les écrans lisaient le store en premier.
    // Ce contexte ne porte que les RÉPONSES du parent et le devis qui en
    // découle. Même partage que les funnels été et stage.
    // Devis SERVEUR : prix mensuel, acompte, solde et date du 1ᵉʳ cours. Le
    // front ne recalcule aucun montant — ce qu'il affiche est ce qui sera
    // débité. Null tant que la formule n'est pas chiffrable.
    quote: null,
    // Les réponses ci-dessus sont écrites dès le geste, le devis seulement à
    // la réponse du serveur : vrai tant qu'un enregistrement est attendu. Une
    // page rechargée entre les deux le redemande au lieu d'afficher le devis
    // d'avant sous les réponses d'après.
    quoteStale: false,
    paid: false,
    // Jeton `?user=` déjà ARBITRÉ par l'écran de reprise — écrit dans le MÊME
    // patch que l'hydratation, jamais ailleurs. Il dit à la garde qu'un
    // `funnelStateId` absent est une RÉPONSE du serveur (lead sans dossier),
    // pas un contexte froid : sans lui, la garde renvoyait ce lead vers la
    // reprise en boucle (resume ⇄ start) jusqu'au crash React #185 — le cas
    // de la bannière « Reprenez l'accompagnement » des familles été.
    resumedToken: null,
}

const loadFromStorage = () => {
    if (Platform.OS !== "web") return null
    try {
        const raw = window?.sessionStorage?.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (_e) {
        return null
    }
}

const saveToStorage = (state) => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (_e) {
        /* noop */
    }
}

const clearStorage = () => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.removeItem(STORAGE_KEY)
    } catch (_e) {
        /* noop */
    }
}

const InscriptionFunnelContext = createContext(null)

/**
 * Provider UNIQUE du funnel Inscription — monté une seule fois au-dessus du
 * navigateur racine (RootNavigator), jamais par écran. Même architecture que
 * les providers Summer et Stage : l'état vit en mémoire et est partagé entre
 * toutes les étapes (web ET natif) ; le sessionStorage n'est qu'un filet de
 * survie au refresh, relu au boot, jamais un canal cross-écran.
 */
export const InscriptionFunnelProvider = ({ children }) => {
    const [state, setState] = useState(() => ({
        ...INSCRIPTION_FUNNEL_INITIAL,
        ...(loadFromStorage() || {}),
    }))

    const update = useCallback((patch) => {
        setState((prev) => {
            const next = { ...prev, ...patch }
            saveToStorage(next)
            return next
        })
    }, [])

    const reset = useCallback(() => {
        clearStorage()
        setState(INSCRIPTION_FUNNEL_INITIAL)
    }, [])

    const value = useMemo(
        () => ({ state, update, reset }),
        [state, update, reset]
    )

    return (
        <InscriptionFunnelContext.Provider value={value}>
            {children}
        </InscriptionFunnelContext.Provider>
    )
}

export const useInscriptionFunnel = () => {
    const ctx = useContext(InscriptionFunnelContext)
    if (ctx) return ctx
    return {
        state: INSCRIPTION_FUNNEL_INITIAL,
        update: () => {},
        reset: () => {},
    }
}
